import React from 'react';
import styled, { ColorsTypes } from 'styled-components';

import { Box, ConditionalLink, Flex, Icons, Typography } from 'components';
import { IconsTypes } from 'components/icons';
import { createUrl, formatDate } from 'app/utils';
import { RecommendationsItemState } from 'features/recommendations/types';

const ReportButton = styled(ConditionalLink)`
  display: flex;
  align-items: center;
  position: relative;
  border: 0;
  padding: 0 16px;
  text-align: left;
  outline: none;
`;

const ReportButtonLabel = styled.span`
  ${({ theme }) => theme.fontTypes.type3}
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-weight: 600;
`;

const ReportButtonIcon = styled(Icons)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  right: 15px;
`;

const CardSiding = styled(Flex)`
  position: absolute;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.gray_600};
  z-index: 10;
  border-radius: 4px;
  display: none;
  padding: 0 15px;
  align-items: center;
`;

const CardIcon = styled(Icons)`
  width: 24px;
  color: ${({ theme }) => theme.colors.gray_300};
`;

const CardWrapper = styled(Flex)`
  border: 1px solid ${({ theme }) => theme.colors.gray_300};
  border-radius: 4px;
  justify-content: space-between;
  position: relative;

  ${(props: any) => {
    if (props['data-view'] === 'grid') {
      return `
        flex-direction: column;
        width: 278px;
        min-height: 340px;

        ${ReportButton} {
          height: 50px;
          border-bottom-left-radius: 2px;
          border-bottom-right-radius: 2px;
        }
      `;
    }

    return `
      flex-direction: row;
      min-height: 60px;
      
      ${ReportButton} {
        
        flex-shrink: 0;
        border-top-right-radius: 2px;
        border-bottom-right-radius: 2px;

        ${props.theme.mediaQueries.mobile} {
          width: 50px;

          ${ReportButtonLabel} {
            display: none;
          }
        }

        ${props.theme.mediaQueries.tablet} {
          width: 250px;

          ${ReportButtonLabel} {
            display: inline-block;
          }
        }
      }
    `;
  }}

  ${(props: any) => {
    if (props['data-status'] === 'scheduled') {
      return `
        ${ReportButton} {
          color: ${props.theme.colors.gray_500};
          background: ${props.theme.colors.gray_100};
          cursor: wait;
        }
        
        &:hover {
          ${ReportButton} {
            color: ${props.theme.colors.white};
            background: ${props.theme.colors.gray_500};
          }
        }
      `;
    }

    if (props['data-status'] === 'available') {
      return `
        ${ReportButton} {
          color: ${props.theme.colors.primary_400};
          background: ${props.theme.colors.gray_100};
          cursor: pointer;
        }

        &:hover {
          border-color: ${props.theme.colors.primary_400};

          ${ReportButton} {
              color: ${props.theme.colors.white};
              background: ${props.theme.colors.primary_400};
          }
        }

      `;
    }

    if (props['data-status'] === 'unavailable') {
      return `
        ${ReportButton} {
          color: ${props.theme.colors.primary_400};
          background: ${props.theme.colors.gray_100};
          cursor: not-allowed;
        }

        &:hover {
          ${CardSiding} {
            display: flex;
          }
        }

      `;
    }

    if (props['data-status'] === 'expired') {
      return `
        ${ReportButton} {
          color: ${props.theme.colors.gray_500};
          background: ${props.theme.colors.gray_100};
          cursor: not-allowed;
        }
        
        &:hover {
          ${ReportButton} {
            color: ${props.theme.colors.white};
            background: ${props.theme.colors.gray_500};
          }
        }
      `;
    }
  }}
`;

const TagUpdated = styled('div')`
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.primary_400};
  width: ${(props: any) => `${props['data-width']}px`};
  height: 22px;
  padding: 0 7px;
  margin-bottom: 10px;

  text-transform: uppercase;
  border-radius: 15px;
  font-size: 12px;
  font-weight: bold;
  color: white;

  &::before {
    content: '';
    display: block;
    width: 8px;
    height: 8px;
    background: white;
    border-radius: 4px;
    margin-right: 7px;
  }
`;

interface RecommendationsCardProps {
  recommendation: RecommendationsItemState;
  productSlug: string;
  productClassUuid: string;
}

const RecommendationsCard = ({ recommendation, productSlug, productClassUuid }: RecommendationsCardProps) => {
  let buttonIcon: IconsTypes = 'arrowRight';
  let buttonLabel = 'ACESSAR';
  let typeColor: ColorsTypes = 'dark_350';

  const view = 'grid';

  return (
    <CardWrapper data-view="grid" data-status="available" flexWrap="nowrap">
      <Flex flexGrow={1} flexDirection="column" justifyContent="space-between">
        <Flex flexGrow={0} justifyContent="space-between" flexWrap="nowrap">
          <Flex flexGrow={1} flexDirection={view === 'grid' ? 'column' : { m: 'column', t: 'row' }} justifyContent="space-between">
            <Flex
              flexGrow={0}
              flexDirection={view === 'grid' ? { m: 'row' } : { m: 'column', t: 'row' }}
              px={view === 'grid' ? { m: 3 } : { m: 2 }}
              pt={view === 'grid' ? { m: 3 } : { m: 2 }}
            >
              <Typography as="span" type={view === 'grid' ? 'type3' : 'type1'} fontWeight="bold" color={typeColor}>
                {recommendation.title}
              </Typography>
            </Flex>

            <Box flexGrow={0} px={view === 'grid' ? { m: 3 } : { m: 2, t: 3 }}>
              <Typography as="span" type="type0" color="gray_500">
                ATUALIZADO: {formatDate(recommendation.updatedAt).format('DD/MM/YYYY [às] HH[h]mm')}
              </Typography>
            </Box>
          </Flex>

          {view === 'grid' && (
            <Flex flexGrow={0} alignItems="start" pt={3} pr={3}>
              <CardIcon icon="fileDocumentOutline" />
            </Flex>
          )}
        </Flex>

        <Flex flexGrow={0} flexDirection="column" px={view === 'grid' ? { m: 3 } : { m: 2 }} pb={view === 'grid' ? { m: 3 } : { m: 2 }}>
          {recommendation.viewedAt && recommendation.highlighted && <TagUpdated data-width={115}>Novo status</TagUpdated>}
          {!recommendation.viewedAt && <TagUpdated data-width={65}>Novo</TagUpdated>}

          {view === 'grid' && (
            <Typography as="span" type="type1" color="gray_500" mb={1}>
              {recommendation.companyName}
            </Typography>
          )}

          <Typography
            as="span"
            type="type5"
            color={typeColor}
            lineHeight={1.2}
            fontWeight="bold"
            mt={view === 'grid' ? { m: 0, t: 1 } : { m: 0, t: 1 }}
          >
            {recommendation.symbolCode}
          </Typography>
        </Flex>
      </Flex>

      <ReportButton to={createUrl('recommendations/details', [productSlug, productClassUuid, recommendation.uuid])} type="internal">
        <ReportButtonLabel>{buttonLabel}</ReportButtonLabel>
        {Boolean(buttonIcon) && <ReportButtonIcon icon={buttonIcon} />}
      </ReportButton>
    </CardWrapper>
  );
};

export default RecommendationsCard;
